import { useState, useEffect, useRef } from 'react';
import * as pdfjs from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { trackError } from 'Utils/errorMonitoring';
import { useTranslation } from 'react-i18next';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

const LOAD_TIMEOUT_MS = 30_000;
const MAX_PASSWORD_ATTEMPTS = 3;

const normalizeFileUrl = (url) => {
    if (!url) return null;
    const urlParts = url.split('?');
    return urlParts[0];
};

export default function usePDFLoader(url) {
    const [pdf, setPdf] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const urlLoadedRef = useRef(null);

    const { t } = useTranslation('common');

    useEffect(() => {
        const normalizedUrl = normalizeFileUrl(url);

        if (!normalizedUrl || normalizedUrl === urlLoadedRef.current) {
            return;
        }

        let cancelled = false;
        let loader = null;

        async function loadPdf() {
            setIsLoading(true);
            setHasError(false);
            urlLoadedRef.current = normalizedUrl;

            loader = pdfjs.getDocument({
                url,
                disableFontFace: false,
                useSystemFonts: false,
                verbosity: 0,
            });

            let passwordAttempts = 0;

            loader.onPassword = (callback, reason) => {
                if (cancelled) return;

                switch (reason) {
                    case pdfjs.PasswordResponses.NEED_PASSWORD: {
                        const password = prompt(
                            t('please-enter-the-password-to-open-this-pdf-file-press-cancel-to-exit'),
                        );
                        if (password === null) {
                            setHasError(true);
                            setIsLoading(false);
                            loader.destroy();
                            return;
                        }
                        passwordAttempts++;
                        callback(password);
                        break;
                    }
                    case pdfjs.PasswordResponses.INCORRECT_PASSWORD: {
                        if (passwordAttempts >= MAX_PASSWORD_ATTEMPTS) {
                            setHasError(true);
                            setIsLoading(false);
                            loader.destroy();
                            return;
                        }
                        const password = prompt(t('invalid-password-please-try-again'));
                        if (password === null) {
                            setHasError(true);
                            setIsLoading(false);
                            loader.destroy();
                            return;
                        }
                        passwordAttempts++;
                        callback(password);
                        break;
                    }
                    default:
                        break;
                }
            };

            try {
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error(`Error: PDF load timeout after ${LOAD_TIMEOUT_MS}ms, URL: ${normalizedUrl}`)), LOAD_TIMEOUT_MS),
                );
                const pdfInstance = await Promise.race([loader.promise, timeoutPromise]);

                if (cancelled) {
                    pdfInstance.destroy();
                    return;
                }

                setPdf((currentPdf) => {
                    if (currentPdf) {
                        currentPdf.destroy();
                    }
                    return pdfInstance;
                });
                setIsLoading(false);
            } catch (error) {
                if (cancelled) return;
                setHasError(true);
                setIsLoading(false);
                trackError(error);
            }
        }

        loadPdf();

        return () => {
            cancelled = true;
            if (loader) {
                loader.destroy();
            }
        };
    }, [url, t]);

    useEffect(() => {
        return () => {
            if (pdf) {
                pdf.destroy();
            }
        };
    }, [pdf]);

    return { pdf, isLoading, hasError };
}
